import React, { useEffect } from 'react';
import './creator-signup.css';
import HeaderSection from '../../../../components/layout/header';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { UNDER_REVIEW_INFO_ITEMS, UNDER_REVIEW_VERIFICATION_STEPS } from '../../../../shared/constants/data';

// Store original body styles for restoration
let originalBodyStyles = {};

// Function to prevent body scroll
const preventBodyScroll = () => {
  // Store original styles
  originalBodyStyles = {
    overflow: document.body.style.overflow,
    position: document.body.style.position,
    top: document.body.style.top,
    width: document.body.style.width
  };

  // Get current scroll position
  const scrollY = window.scrollY;

  // Apply styles to prevent scrolling
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.width = '100%';
};

// Function to restore body scroll
const restoreBodyScroll = () => {
  // Restore original styles
  document.body.style.overflow = originalBodyStyles.overflow || '';
  document.body.style.position = originalBodyStyles.position || '';
  document.body.style.top = originalBodyStyles.top || '';
  document.body.style.width = originalBodyStyles.width || '';

  // Restore scroll position
  if (originalBodyStyles.top) {
    const scrollY = parseInt(originalBodyStyles.top.replace('-', '').replace('px', ''));
    window.scrollTo(0, scrollY);
  }
};

/**
 * UnderReview component displays a verification status screen for creators
 * whose accounts are under review after signup
 */
const UnderReview = ({ onBack }) => {
  const navigate = useNavigation();

  // Prevent body scroll when component mounts and restore when unmounts
  useEffect(() => {
    // Prevent scrolling when component mounts
    preventBodyScroll();

    // Restore scrolling when component unmounts
    return () => {
      restoreBodyScroll();
    };
  }, []);

  const handleLogin = () => {
    navigate.toLogin();
  };

  return (
    <div className="under-review-screen">
      {/* Blurred Background Content */}
      <div className="background-content">
        {/* Header */}
        <HeaderSection
          type="text"
          title="Register"
        />

        {/* Main Content (Blurred) */}
        <main className="under-review-main-bg mt-5">
          <div className="under-review-container-bg">
            <div className="under-review-welcome-bg">
              <div className="under-review-icon-bg">
                <i style={{ height: "1.4rem" }} className="fas fa-user-plus"></i>
              </div>
              <h2 className="under-review-subtitle-bg">Create Your Account</h2>
              <p className="under-review-description-bg">
                Enter your name and birthdate exactly as shown on your ID or passport for verification
              </p>
            </div>

            {/* Form placeholder content */}
            <div className="form-placeholder">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="form-section-placeholder"></div>
              ))}
            </div>
          </div>
        </main>
      </div>

      {/* Popup Overlay */}
      <div className="review-popup-overlay">
        <div className="review-popup">

          {/* Status Icon */}
          <div className="review-status-icon-container">
            <div className="review-status-icon">
              <i className="fas fa-clock"></i>
            </div>
          </div>

          {/* Main Heading */}
          <div className="review-main-heading">
            <h2 className="review-title">Under Review </h2>

            {/* Description Text */}
            <div className="review-description">
              <p className="review-description-text">
                Hang tight! We'll email you once your account is verified. Creators are{' '}
                <span className="review-time-highlight">usually verified within 30 minutes</span> ⏳
              </p>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className='review-progress-scrollable'>
            <div className="review-progress-section">
              <div className="review-progress-header">
                <span className="review-progress-label">Verification Progress</span>
                <span className="review-progress-status">Processing...</span>
              </div>
              <div className="review-progress-bar-container">
                <div className="review-progress-bar"></div>
              </div>
            </div>

            {/* Status Steps */}
            <div className="review-status-steps">
              {UNDER_REVIEW_VERIFICATION_STEPS.map((step) => (
                <div key={step.id} className={`review-step ${step.status}`}>
                  <div className={`review-step-icon ${step.status}`}>
                    {step.status === 'completed' ? (
                      <i className="fas fa-check"></i>
                    ) : step.status === 'in-progress' ? (
                      <div className="review-step-dot"></div>
                    ) : (
                      <div className="review-step-dot-inactive"></div>
                    )}
                  </div>
                  <span className={`review-step-text ${step.status === 'pending' ? 'pending' : ''}`}>
                    {step.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Additional Info */}
            <div className="review-info-section">
              <div className="review-info-container">
                <div className="review-info-icon">
                  <i className="fas fa-circle-info"></i>
                </div>
                <div className="review-info-content">
                  <p className="review-info-title">
                    <strong>What happens next?</strong>
                  </p>
                  <ul className="review-info-list">
                    {UNDER_REVIEW_INFO_ITEMS.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Email Reminder Box */}
            <div className="review-email-section">
              <div className="review-email-container">
                <div className="review-email-icon">
                  <i className="fas fa-envelope-open-text"></i>
                </div>
                <div className="review-email-content">
                  <p className="review-email-title">
                    <strong>Don't see our email?</strong>
                  </p>
                  <p className="review-email-text">
                    Make sure to check your <span className="review-email-highlight">spam/junk folder</span> as verification emails sometimes end up there.
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="review-actions">
            <button onClick={handleLogin} className="review-login-btn">
              <span className="review-login-btn-content">
                <i className="fas fa-right-to-bracket"></i>
                <span>Login</span>
              </span>
            </button>

            <a
              href="mailto:support@juicyhouse.com?subject=Contact%20Support"
              className="review-help-btn"
            >
              <i className="fas fa-circle-question"></i>
              <span>Get Help</span>
            </a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UnderReview; 